import { getDocument } from '@atlaskit/browser-apis';

const CHUNK_LOAD_ERROR_MESSAGES = [
	'Failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'Importing a module script failed',
	'Cannot find module',
];

type PerformanceResourceTimingWithResponseStatus = PerformanceResourceTiming & {
	responseStatus?: number;
};

type DocumentWithLifecycleDiagnostics = Document & {
	prerendering?: boolean;
	wasDiscarded?: boolean;
};

type NavigatorWithServiceWorker = Navigator & {
	serviceWorker?: {
		controller?: unknown;
	};
};

const getFailedResourceUrl = (message: string): string | undefined => {
	return message.match(/https?:\/\/[^\s]+/)?.[0];
};

const getFailedResourceName = (url: string): string | undefined => {
	try {
		return new URL(url).pathname.split('/').filter(Boolean).at(-1);
	} catch {
		return undefined;
	}
};

const getResourceTiming = (
	url: string,
): PerformanceResourceTimingWithResponseStatus | undefined => {
	if (typeof performance === 'undefined' || typeof performance.getEntriesByName !== 'function') {
		return undefined;
	}

	return performance
		.getEntriesByName(url, 'resource')
		.filter((entry): entry is PerformanceResourceTiming => entry.entryType === 'resource')
		.at(-1);
};

/**
 * Builds browser and page-lifecycle diagnostics without including URLs, user-agent strings, or
 * other high-cardinality browser identifiers.
 */
export const getRuntimeDiagnostics = (): Record<string, unknown> => {
	const document = getDocument() as DocumentWithLifecycleDiagnostics | undefined;
	const canReadPerformanceEntries =
		typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function';
	const navigationEntry = canReadPerformanceEntries
		? (performance.getEntriesByType('navigation').at(-1) as
				| PerformanceNavigationTimingWithResponseStatus
				| undefined)
		: undefined;
	const resourceEntries = canReadPerformanceEntries
		? performance
				.getEntriesByType('resource')
				.filter((entry): entry is PerformanceResourceTiming => entry.entryType === 'resource')
		: [];
	const scriptEntries = resourceEntries.filter((entry) => entry.initiatorType === 'script');
	const cachedScriptEntries = scriptEntries.filter(
		(entry) => entry.transferSize === 0 && entry.decodedBodySize > 0,
	);
	const navigationResponseStatus = navigationEntry?.responseStatus;
	const navigatorWithServiceWorker =
		typeof navigator !== 'undefined' ? (navigator as NavigatorWithServiceWorker) : undefined;

	return {
		...(navigatorWithServiceWorker
			? {
					browserOnline: navigatorWithServiceWorker.onLine,
					serviceWorkerControlled: Boolean(navigatorWithServiceWorker.serviceWorker?.controller),
				}
			: {}),
		...(document
			? {
					documentPrerendering: Boolean(document.prerendering),
					documentVisibilityState: document.visibilityState,
					documentWasDiscarded: Boolean(document.wasDiscarded),
				}
			: {}),
		...(navigationEntry
			? {
					navigationDuration: Math.round(navigationEntry.duration),
					navigationProtocol: navigationEntry.nextHopProtocol,
					navigationType: navigationEntry.type,
				}
			: {}),
		...(typeof navigationResponseStatus === 'number' && navigationResponseStatus > 0
			? { navigationResponseStatus }
			: {}),
		performanceCachedScriptCount: cachedScriptEntries.length,
		performanceResourceCount: resourceEntries.length,
		performanceScriptCount: scriptEntries.length,
	};
};

type PerformanceNavigationTimingWithResponseStatus = PerformanceNavigationTiming & {
	responseStatus?: number;
};

/**
 * Builds a small, URL-safe diagnostic payload for Forge renderer failures.
 * The full error message and resource URL are intentionally excluded because
 * Forge asset URLs can contain signed context tokens.
 */
export const getErrorDiagnostics = (error: Error): Record<string, unknown> => {
	const failedResourceUrl = getFailedResourceUrl(error.message);
	const failedResourceName = failedResourceUrl
		? getFailedResourceName(failedResourceUrl)
		: undefined;
	const resourceTimingEntries =
		failedResourceUrl &&
		typeof performance !== 'undefined' &&
		typeof performance.getEntriesByName === 'function'
			? performance
					.getEntriesByName(failedResourceUrl, 'resource')
					.filter((entry): entry is PerformanceResourceTiming => entry.entryType === 'resource')
			: [];
	const resourceTiming = failedResourceUrl ? getResourceTiming(failedResourceUrl) : undefined;
	const responseStatus = resourceTiming?.responseStatus;

	return {
		...getRuntimeDiagnostics(),
		errorCategory:
			error.name === 'ChunkLoadError' ||
			CHUNK_LOAD_ERROR_MESSAGES.some((message) => error.message.includes(message))
				? 'chunk-load'
				: 'runtime-error',
		...(failedResourceName ? { failedResourceName } : {}),
		...(failedResourceUrl
			? {
					resourceTimingEntryCount: resourceTimingEntries.length,
					resourceTimingFound: Boolean(resourceTiming),
				}
			: {}),
		...(resourceTiming
			? {
					resourceDuration: Math.round(resourceTiming.duration),
					resourceTransferSize: resourceTiming.transferSize,
					resourceEncodedBodySize: resourceTiming.encodedBodySize,
					resourceDecodedBodySize: resourceTiming.decodedBodySize,
					resourceFromCache:
						resourceTiming.transferSize === 0 && resourceTiming.decodedBodySize > 0,
					resourceInitiatorType: resourceTiming.initiatorType,
					resourceProtocol: resourceTiming.nextHopProtocol,
					resourceRequestStart: Math.round(resourceTiming.requestStart),
					resourceResponseDuration: Math.round(
						resourceTiming.responseEnd - resourceTiming.responseStart,
					),
					resourceResponseStart: Math.round(resourceTiming.responseStart),
					resourceStartTime: Math.round(resourceTiming.startTime),
				}
			: {}),
		...(typeof responseStatus === 'number' && responseStatus > 0
			? { resourceResponseStatus: responseStatus }
			: {}),
	};
};
